import React, { Fragment } from "react"
import { Button, Container, Grid, TextField, InputLabel, Select, MenuItem, FormControlLabel, Checkbox, Typography, Dialog, DialogTitle, DialogContent, DialogActions, Link } from "@material-ui/core"
import { Redirect } from "react-router-dom"
import { getConfigSync } from "../../api"
import request from "../../api"
import countryList from 'react-select-country-list'

interface Props {}

interface State {
  howHeardAbout:string
  howHeardAboutOther:string
  confirmReadNotes:boolean
  confirmTeacher:boolean
  name:string
  email:string
  emailError:string
  school:string
  website:string
  urn:string
  consent1:boolean
  consent2:boolean
  phoneNumber:string
  addressLine1:string
  addressLine2:string
  addressCity:string
  addressState:string
  addressCountry:string
  addressPostcode:string
  schoolType:"Non-fee-paying"|"Fee-paying"|""
  submitting:boolean
  message:string
  error:string
  showSuccessDialog:boolean
  showErrorDialog:boolean
  submitted:boolean
}

export default class TeacherRegisterPage extends React.Component<Props, State> {
  constructor(props:Props) {
    super(props)
    this.state = {
      howHeardAbout:'',
      howHeardAboutOther:'',
      confirmReadNotes:false,
      confirmTeacher:false,
      name:'',
      email:'',
      emailError:'',
      school:'',
      website:'',
      urn:'',
      consent1:false,
      consent2:false,
      phoneNumber:'',
      addressLine1:'',
      addressLine2:'',
      addressCity:'',
      addressState:'',
      addressCountry:'',
      addressPostcode:'',
      schoolType:"",
      submitting:false,
      message:'',
      error:'',
      showSuccessDialog:false,
      showErrorDialog:false,
      submitted:false
    }
  }

  validateEmail = () => {
    const FREE_EMAIL_DOMAINS = [
      'gmail.com',
      'hotmail.com',
      'outlook.com',
      'yahoo.com',
      'live.com',
      'icloud.com',
      'gmx.com',
      'proton.me',
      'protonmail.com'
    ]

    if (!this.state.email) {
      this.setState({ emailError: '' })
      return
    }

    const emailLower = this.state.email.toLowerCase().replace(/\s+/g, '')
    const atIndex = emailLower.indexOf('@')

    if (atIndex === -1 || atIndex === emailLower.length - 1) {
      this.setState({ emailError: 'Invalid email format' })
      return
    }

    const emailDomain = emailLower.substring(atIndex + 1)

    // Check for free email domains
    if (FREE_EMAIL_DOMAINS.indexOf(emailDomain) !== -1) {
      this.setState({ emailError: 'Personal email addresses (Gmail, Yahoo, etc.) are not accepted. Please use an official school email address.' })
      return
    }

    this.setState({ emailError: '' })
  }

  render() {
    if (getConfigSync().competitionShortName === 'BPhO') {
      return <Redirect to="/login" />
    }

    const canSubmit = !!this.state.name && !!this.state.email && !!this.state.school && !!this.state.website && !!this.state.addressLine1 && !!this.state.addressCity && !!this.state.addressCountry && !!this.state.schoolType && this.state.confirmReadNotes && this.state.confirmTeacher && this.state.consent1 && !!this.state.howHeardAbout && !this.state.submitting && !this.state.submitted && !this.state.emailError

    return (
        <Container maxWidth="md">
          <Grid container direction="column" alignItems="center" spacing={2}>
            <Grid item>
              <h1>{getConfigSync().competitionShortName} Sign up</h1>
            </Grid>
            <Grid item>
              <Typography variant="body1" style={{marginBottom: 16}}>
                <strong>Dear Teacher,</strong>
              </Typography>
              <Typography variant="body2" style={{marginBottom: 8}}>
                This form must only be used for schools which do not already have an account.
              </Typography>
              <Typography variant="body2" style={{marginBottom: 8}}>
                It will take up to ten working days after submitting this form before you have access to the exam portal.
              </Typography>
              <Typography variant="body2" style={{marginBottom: 8}}>
                If there has been a change in the person responsible for your school's account, please email contact@ukbiologycompetitions.org.
              </Typography>
              <Typography variant="body2" style={{marginBottom: 8}}>
                Within the exam portal, you will be able to register your school for our competitions. Detailed instructions can be found in the relevant FAQ sections of the website.
              </Typography>
              <Typography variant="body2" style={{marginBottom: 16}}>
                Please note that schools in the following countries need to register through ASEEDER (see link on the sign up page):<br/>
                China, Vietnam, Thailand, Indonesia, Malaysia, Singapore, Mongolia, South Korea.
              </Typography>
              <Typography variant="body2" style={{marginBottom: 16}}>
                Kind regards,<br/>
                {getConfigSync().competitionShortName}
              </Typography>
            </Grid>
          </Grid>
          <form noValidate onSubmit={this.onSubmit}>
            <Grid container direction="column" spacing={2}>
              <Grid item>
                <InputLabel id="howHeard-label">Please let us know how you heard about the competitions. *</InputLabel>
                <Select
                    labelId="howHeard-label"
                    value={this.state.howHeardAbout}
                    onChange={e => this.setState({ howHeardAbout: e.target.value as string })}
                    fullWidth
                    required
                >
                  <MenuItem value="">Select...</MenuItem>
                  <MenuItem value="Internet search for biology competitions">Internet search for biology competitions</MenuItem>
                  <MenuItem value="Association for Science Education advertisement">Association for Science Education advertisement</MenuItem>
                  <MenuItem value="Biotutors (JISCMAIL discussion group)">Biotutors (JISCMAIL discussion group)</MenuItem>
                  <MenuItem value="Previous school">Previous school</MenuItem>
                  <MenuItem value="Other">Other</MenuItem>
                </Select>
              </Grid>
              {this.state.howHeardAbout === "Other" && (
                  <Grid item>
                    <TextField margin="dense" label="If the answer above was 'other', please provide details." fullWidth value={this.state.howHeardAboutOther} onChange={e => this.setState({ howHeardAboutOther: e.target.value })} />
                  </Grid>
              )}
              <Grid item>
                <FormControlLabel
                    control={
                      <Checkbox
                          checked={this.state.confirmReadNotes}
                          onChange={e => this.setState({ confirmReadNotes: e.target.checked })}
                          color="primary"
                      />
                    }
                    label="I confirm that I have read the explanatory notes and am aware that this form is only to be used if my school is not already registered. *"
                />
              </Grid>
              <Grid item>
                <FormControlLabel
                    control={
                      <Checkbox
                          checked={this.state.confirmTeacher}
                          onChange={e => this.setState({ confirmTeacher: e.target.checked })}
                          color="primary"
                      />
                    }
                    label="I confirm I am a teacher at a school or registered exam centre. *"
                />
              </Grid>
              <Grid item>
                <TextField margin="dense" label="Full name *" fullWidth value={this.state.name} onChange={e => this.setState({ name: e.target.value })} />
              </Grid>
              <Grid item>
                <TextField
                    margin="dense"
                    label="Email *"
                    fullWidth
                    value={this.state.email}
                    onChange={e => {
                      this.setState({ email: e.target.value }, () => {
                        // Validate after a short delay to avoid excessive validation
                        setTimeout(() => this.validateEmail(), 300)
                      })
                    }}
                    onBlur={this.validateEmail}
                    error={!!this.state.emailError}
                    helperText={this.state.emailError || "You MUST use an official school email address matching your school's domain. Personal email addresses (Gmail, Yahoo, etc.) or emails from different domains will be rejected."}
                />
              </Grid>
              <Grid item>
                <TextField margin="dense" label="Name of school *" fullWidth value={this.state.school} onChange={e => this.setState({ school: e.target.value })} />
              </Grid>
              <Grid item>
                <TextField
                    margin="dense"
                    label="School Website *"
                    fullWidth
                    value={this.state.website}
                    onChange={e => {
                      this.setState({ website: e.target.value }, () => {
                        // Re-validate email when website changes
                        if (this.state.email) {
                          setTimeout(() => this.validateEmail(), 300)
                        }
                      })
                    }}
                />
              </Grid>
              <Grid item>
                <TextField margin="dense" label="School URN number" fullWidth value={this.state.urn} onChange={e => this.setState({ urn: e.target.value })} helperText="Required for schools in England & Wales and British Schools Overseas - a six digit number allocated by the government, NOT the exam centre number." />
              </Grid>
              <Grid item>
                <TextField margin="dense" label="Telephone number" fullWidth value={this.state.phoneNumber} onChange={e => this.setState({ phoneNumber: e.target.value })} />
              </Grid>
              <Grid item>
                <TextField margin="dense" label="Address line 1 *" fullWidth value={this.state.addressLine1} onChange={e => this.setState({ addressLine1: e.target.value })} />
              </Grid>
              <Grid item>
                <TextField margin="dense" label="Address line 2" fullWidth value={this.state.addressLine2} onChange={e => this.setState({ addressLine2: e.target.value })} />
              </Grid>
              <Grid item>
                <TextField margin="dense" label="City *" fullWidth value={this.state.addressCity} onChange={e => this.setState({ addressCity: e.target.value })} />
              </Grid>
              <Grid item>
                <TextField margin="dense" label="County / State" fullWidth value={this.state.addressState} onChange={e => this.setState({ addressState: e.target.value })} />
              </Grid>
              <Grid item>
                <InputLabel id="country-label">Country *</InputLabel>
                <Select
                    labelId="country-label"
                    value={this.state.addressCountry}
                    onChange={e => this.setState({ addressCountry: e.target.value as string })}
                    fullWidth
                >
                  <MenuItem value="">Select...</MenuItem>
                  <MenuItem value={"GB"}>United Kingdom</MenuItem>
                  {countryList().getData().map((o, i) => (
                      <MenuItem key={i} value={o.value}>{o.label}</MenuItem>
                  ))}
                </Select>
              </Grid>
              <Grid item>
                <TextField margin="dense" label="Postcode" fullWidth value={this.state.addressPostcode} onChange={e => this.setState({ addressPostcode: e.target.value })} />
              </Grid>
              <Grid item>
                <InputLabel id="schooltype-label">School type *</InputLabel>
                <Select
                    labelId="schooltype-label"
                    value={this.state.schoolType}
                    onChange={e => this.setState({ schoolType: e.target.value as any })}
                    fullWidth
                    required
                >
                  <MenuItem value="">Select...</MenuItem>
                  <MenuItem value={"Non-fee-paying"}>Non-fee-paying</MenuItem>
                  <MenuItem value={"Fee-paying"}>Fee-paying</MenuItem>
                </Select>
              </Grid>
              <Grid item>
                <FormControlLabel
                    control={
                      <Checkbox
                          checked={this.state.consent1}
                          onChange={e => this.setState({ consent1: e.target.checked })}
                          color="primary"
                      />
                    }
                    label={
                      <span>
                    I confirm that I have read and understood the UKBC Privacy Policy and acknowledge that my data will be processed for the purposes of administering school accounts and competitions in accordance with that policy. Please find the privacy policy at{' '}
                        <Link href="http://ukbiologycompetitions.org/privacy-policy/" target="_blank" rel="noopener noreferrer">
                      http://ukbiologycompetitions.org/privacy-policy/
                    </Link>{' '}*
                  </span>
                    }
                />
              </Grid>
              <Grid item>
                <FormControlLabel
                    control={
                      <Checkbox
                          checked={this.state.consent2}
                          onChange={e => this.setState({ consent2: e.target.checked })}
                          color="primary"
                      />
                    }
                    label={
                      <span>
                    I would like to receive updates about additional UKBC initiatives, events and related educational opportunities beyond essential competition communications. Please find the privacy policy at{' '}
                        <Link href="http://ukbiologycompetitions.org/privacy-policy/" target="_blank" rel="noopener noreferrer">
                      http://ukbiologycompetitions.org/privacy-policy/
                    </Link>
                  </span>
                    }
                />
              </Grid>
              {!this.state.submitted && (
                  <Grid item>
                    <Button type="submit" variant="outlined" size="large" disabled={!canSubmit}>Submit registration</Button>
                  </Grid>
              )}
            </Grid>
          </form>
          <Dialog open={this.state.showSuccessDialog} onClose={() => this.setState({ showSuccessDialog: false })}>
            <DialogTitle>Registration Submitted</DialogTitle>
            <DialogContent>
              <Typography style={{ color: 'green' }}>
                Thank you for submitting your application. We will verify the details and get back to you if we require any further information. You will receive a confirmation email once your account is set up, usually within ten working days.
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => this.setState({ showSuccessDialog: false }, () => { window.location.href = '/login' })} color="primary">OK</Button>
            </DialogActions>
          </Dialog>
          <Dialog open={this.state.showErrorDialog} onClose={() => this.setState({ showErrorDialog: false })}>
            <DialogTitle>Registration Error</DialogTitle>
            <DialogContent>
              <Typography style={{ color: 'red' }}>
                {this.state.error}
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => this.setState({ showErrorDialog: false })} color="primary">OK</Button>
            </DialogActions>
          </Dialog>
        </Container>
    )
  }

  onSubmit = async (e:React.FormEvent) => {
    e.preventDefault()
    if (this.state.submitting) return
    this.setState({ submitting:true, error:'', message:'' })
    try {
      let res = await request('/api/public/teachers/register', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          name: this.state.name,
          email: this.state.email,
          school: this.state.school,
          phoneNumber: this.state.phoneNumber,
          addressLine1: this.state.addressLine1,
          addressLine2: this.state.addressLine2,
          addressCity: this.state.addressCity,
          addressState: this.state.addressState,
          addressCountry: this.state.addressCountry,
          addressPostcode: this.state.addressPostcode,
          schoolType: this.state.schoolType,
          urn: this.state.urn,
          website: this.state.website,
          howHeardAbout: this.state.howHeardAbout,
          howHeardAboutOther: this.state.howHeardAboutOther,
          confirmReadNotes: this.state.confirmReadNotes,
          confirmTeacher: this.state.confirmTeacher,
          consent1: this.state.consent1,
          consent2: this.state.consent2
        })
      })
      if (!res.ok) {
        let text = await res.text()
        console.error('[teacher-register] server rejected registration', {
          status: res.status,
          statusText: res.statusText,
          body: text || null,
          email: this.state.email,
          school: this.state.school
        })
        this.setState({
          error: text || 'Registration failed',
          submitting:false,
          showErrorDialog: true
        })
        return
      }
      this.setState({
        submitting:false,
        submitted:true,
        showSuccessDialog:true
      })
    } catch (e) {
      console.error('[teacher-register] request failed before/without usable server response', {
        email: this.state.email,
        school: this.state.school,
        errorName: e && (e as any).name,
        errorMessage: e && (e as any).message,
        errorStack: e && (e as any).stack
      })
      this.setState({
        submitting:false,
        error:'Registration failed',
        showErrorDialog: true
      })
    }
  }
}
